import { useEffect, useState } from 'react';

import { usePathname, useRouter } from 'expo-router';

import { routes } from 'navigation/routing';
import { selectors, useAppSelector } from 'state/global-state';
import { useOrderContext } from 'state/order';
import { useServiceModeLive } from 'state/service-mode';
import { shouldRedirectToMenu } from 'utils/cart-utils';

import { cartHasQualifyingRedirectContent } from '../utils';
import { hasStoredCartEntries } from '../utils/stored-cart-entries';

import { useCartContext } from './use-cart-context';

type UseCartRedirectToMenuParams = {
  enabled?: boolean;
};

export const useCartRedirectToMenu = ({ enabled = true }: UseCartRedirectToMenuParams = {}) => {
  const { cartEntries, isMinimalCartStoreRepricing } = useCartContext();
  const legacyCartEntries = useAppSelector(selectors.ordering.selectCartEntries);
  const pricedOrderId = useAppSelector(selectors.ordering.selectPricedOrderId);
  const pathname = usePathname();
  const router = useRouter();
  const serviceMode = useServiceModeLive();
  const { serverOrder } = useOrderContext();

  const orderServiceMode = serverOrder?.cart?.serviceMode;
  const orderStatus = serverOrder?.status ?? null;

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const isCartRoute = pathname === routes.cart;
    setIsMounted(isCartRoute);
  }, [pathname]);

  const hasQualifyingContent = cartHasQualifyingRedirectContent(cartEntries);
  const hasRecoverableLegacyCartEntries = legacyCartEntries.length > 0;
  const hasRecoverablePricedOrder = Boolean(pricedOrderId);
  // `minimalCartPricedStoreNumber` is not restored by `syncWithStorage`, so right after a hard
  // reload the minimal cart is always still resolving for the store. Don't evaluate the menu
  // redirect until that recovery settles, otherwise a transient unsettled state redirects a cart
  // that actually has recoverable entries. Once repricing completes, empty carts still redirect.
  const isMinimalCartRecovering = isMinimalCartStoreRepricing;
  // Only trust storage as a recovery signal while the in-memory cart is still empty (pre-hydration
  // window). Once hydrated, `shouldRedirectToMenu` must remain the authority so a non-qualifying
  // cart or a missing service mode still redirects to menu.
  const isInMemoryCartEmpty = cartEntries.length === 0 && legacyCartEntries.length === 0;

  useEffect(() => {
    if (
      !(
        enabled &&
        !isMinimalCartRecovering &&
        !hasRecoverableLegacyCartEntries &&
        !hasRecoverablePricedOrder &&
        // On a hard reload this can evaluate before Redux hydrates from storage, so an empty
        // in-memory cart is not proof there is nothing to recover. Consult storage only during that
        // window; once hydrated, `shouldRedirectToMenu` is the authority.
        !(isInMemoryCartEmpty && hasStoredCartEntries()) &&
        shouldRedirectToMenu({
          isMounted,
          cartEntries,
          orderStatus,
          serviceMode,
          orderServiceMode,
          hasItemsOrCombos: hasQualifyingContent,
        })
      )
    ) {
      return;
    }

    // Schedule the redirect, but keep the handles so a later render that recovers the cart
    // (e.g. minimal entries or a priced order hydrate after mount) can cancel it via cleanup.
    // Without this, a redirect scheduled off a transient empty state fires unconditionally.
    let timeoutId: ReturnType<typeof setTimeout>;
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        router.replace(routes.menu);
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [
    cartEntries,
    enabled,
    hasQualifyingContent,
    hasRecoverableLegacyCartEntries,
    hasRecoverablePricedOrder,
    isInMemoryCartEmpty,
    isMinimalCartRecovering,
    isMounted,
    orderServiceMode,
    orderStatus,
    router,
    serviceMode,
  ]);
};
